import { useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  useDynamicCallback,
  useMixpanel,
  useSecuritiesContext,
  type AgGridMenuItems,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { useNavigateToDeepDiveOrMultileg } from '../../../../hooks/useNavigateToDeepDiveOrMultileg';
import { useNavigateToMultilegCombo } from '../../../../hooks/useNavigateToMultilegCombo';
import { getAvailableMultilegTypes } from '../../../../utils/getAvailableMultilegTypes';
import { selectOptionFilter } from './Filter/selectors';
import type { OptionChainDataRow } from './types';
import { isOptionTypeEnum } from './utils';

export function useGetOptionChainContextMenu(panelID: string) {
  const mixpanel = useMixpanel();
  const { securitiesBySymbol } = useSecuritiesContext();
  const marketAccountName = useAppStateSelector(state => selectOptionFilter(state, panelID))?.marketAccountName;
  const navigateToDeepDiveOrMultileg = useNavigateToDeepDiveOrMultileg();
  const navigateToMultilegCombo = useNavigateToMultilegCombo();

  return useDynamicCallback(
    (params: GetContextMenuItemsParams<OptionChainDataRow>): AgGridMenuItems<OptionChainDataRow> => {
      const { column, node } = params;
      const data = node?.data;
      const colDef = column?.getColDef();

      const fieldPath = colDef?.field;
      if (fieldPath == null || !data) {
        return [];
      }
      const [optionType, field] = fieldPath.split('.');

      // If the optionType is wrong or there is no field, do nothing
      if (!isOptionTypeEnum(optionType) || !field) {
        return [];
      }

      const symbol = data[optionType]?.Symbol;
      const security = symbol ? securitiesBySymbol.get(symbol) : undefined;

      if (!security) {
        return [];
      }

      const availableMultilegTypes = getAvailableMultilegTypes(security);
      const createMultilegSubMenu: MenuItemDef[] =
        availableMultilegTypes.length > 0
          ? [
              {
                name: 'Create New Multileg',
                icon: `<i class="ag-icon ${IconName.Plus}"/>`,
                subMenu: availableMultilegTypes.map(option => ({
                  name: option.label,
                  action: () => {
                    mixpanel.track(MixpanelEvent.CreateNewMultileg, {
                      [MixpanelEventProperty.Type]: option.type,
                      [MixpanelEventProperty.Source]: MixpanelEventSource.OptionsChain,
                    });
                    navigateToMultilegCombo({
                      instrumentType: option.type,
                      marketAccountNames: marketAccountName ? [marketAccountName] : undefined,
                      legSymbols: [security.Symbol],
                    });
                  },
                })),
              },
            ]
          : [];

      return [
        {
          name: `Open ${security.DisplaySymbol} DeepDive`,
          icon: `<i class="ag-icon ${IconName.Deepdive}"/>`,
          action: () => {
            mixpanel.track(MixpanelEvent.OpenDeepDive);
            navigateToDeepDiveOrMultileg(
              security,
              marketAccountName ? [marketAccountName] : undefined,
              undefined,
              undefined
            );
          },
        },
        ...createMultilegSubMenu,
      ];
    }
  );
}
